import { useState } from 'react'

import { useTranslation } from 'react-i18next'

import {
  Box,
  HStack,
  SecondaryButton,
  TableBody,
  TableCell,
  TableColumnHeader,
  TableHeader,
  TableRoot,
  TableRow,
  TertiaryButton,
  Text,
  VStack,
} from '@enechain/ecloud-designsystem'

import {
  TABLE_BODY_CELL_STYLE,
  TABLE_HEADER_CELL_STYLE,
} from '~/components/common/MasterDataTable'
import { BasePatternModal } from '~/pages/AdminOcctoMaster/tabs/SplitSupplyContract/components/BasePatternModal'
import {
  type BasePatternValue,
  draftFromPattern,
  newPatternDraft,
} from '~/pages/AdminOcctoMaster/tabs/SplitSupplyContract/models/basePattern'
import {
  patternDayTypesLabel,
  patternMonthsLabel,
  patternPeriodLabel,
  patternSlotRangeLabel,
  patternValuesLabel,
} from '~/pages/AdminOcctoMaster/tabs/SplitSupplyContract/models/labels'

const NS = 'page:adminOcctoMaster.splitSupplyContract.basePattern' as const

type ModalState =
  | { kind: 'closed' }
  | { kind: 'create' }
  | { kind: 'edit'; index: number }

type Props = {
  patterns: BasePatternValue[]
  onChange: (next: BasePatternValue[]) => void
}

/**
 * 契約モーダルの「通告値の基本パターン (48 点)」節。 一覧 + 追加 / 編集 / 削除。
 * 新規登録の契約にも追加できる。 追加・編集・削除は契約フォームの値を書き換えるだけで、
 * 契約の「保存」で契約と一緒に保存する (全置換)。
 */
export const BasePatternsSection = ({
  patterns,
  onChange,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const [modal, setModal] = useState<ModalState>({ kind: 'closed' })

  const sorted = patterns
    .map((pattern, index) => ({ pattern, index }))
    .sort((a, b) => a.pattern.patternOrder - b.pattern.patternOrder)

  const close = (): void => {
    setModal({ kind: 'closed' })
  }

  return (
    <VStack
      align="stretch"
      gap={2}
      padding={3}
      borderWidth="1px"
      borderColor="Border.lowEmphasis"
      borderRadius="md"
      backgroundColor="Surface.secondary"
    >
      <HStack justify="space-between">
        <VStack align="start" gap={0.5}>
          <Text textStyle="label" color="Text.highEmphasis">
            {t(`${NS}.heading`)}
          </Text>
          <Text textStyle="caption" color="Text.midEmphasis">
            {t(`${NS}.hint`)}
          </Text>
        </VStack>
        <SecondaryButton
          onClick={() => {
            setModal({ kind: 'create' })
          }}
          size="sm"
        >
          {t(`${NS}.add`)}
        </SecondaryButton>
      </HStack>

      <Box overflowX="auto">
        <TableRoot fontSize="md" size="sm">
          <TableHeader>
            <TableRow>
              <TableColumnHeader
                style={{ ...TABLE_HEADER_CELL_STYLE, textAlign: 'right' }}
              >
                {t(`${NS}.column.order`)}
              </TableColumnHeader>
              <TableColumnHeader style={TABLE_HEADER_CELL_STYLE}>
                {t(`${NS}.column.period`)}
              </TableColumnHeader>
              <TableColumnHeader style={TABLE_HEADER_CELL_STYLE}>
                {t(`${NS}.column.dayTypes`)}
              </TableColumnHeader>
              <TableColumnHeader style={TABLE_HEADER_CELL_STYLE}>
                {t(`${NS}.column.months`)}
              </TableColumnHeader>
              <TableColumnHeader style={TABLE_HEADER_CELL_STYLE}>
                {t(`${NS}.column.slotRange`)}
              </TableColumnHeader>
              <TableColumnHeader
                style={{ ...TABLE_HEADER_CELL_STYLE, textAlign: 'right' }}
              >
                {t(`${NS}.column.values`)}
              </TableColumnHeader>
              <TableColumnHeader style={TABLE_HEADER_CELL_STYLE} />
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} style={TABLE_BODY_CELL_STYLE}>
                  <Text color="Text.midEmphasis">{t(`${NS}.empty`)}</Text>
                </TableCell>
              </TableRow>
            ) : (
              sorted.map(({ pattern, index }) => (
                <TableRow key={`${pattern.patternOrder}-${index}`}>
                  <TableCell
                    textAlign="right"
                    whiteSpace="nowrap"
                    style={TABLE_BODY_CELL_STYLE}
                  >
                    {pattern.patternOrder}
                  </TableCell>
                  <TableCell whiteSpace="nowrap" style={TABLE_BODY_CELL_STYLE}>
                    {patternPeriodLabel(pattern)}
                  </TableCell>
                  <TableCell style={TABLE_BODY_CELL_STYLE}>
                    {patternDayTypesLabel(t, pattern.dayTypes)}
                  </TableCell>
                  <TableCell style={TABLE_BODY_CELL_STYLE}>
                    {patternMonthsLabel(t, pattern.months)}
                  </TableCell>
                  <TableCell whiteSpace="nowrap" style={TABLE_BODY_CELL_STYLE}>
                    {patternSlotRangeLabel(t, pattern.slotFrom, pattern.slotTo)}
                  </TableCell>
                  <TableCell
                    textAlign="right"
                    style={{
                      ...TABLE_BODY_CELL_STYLE,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {patternValuesLabel(t, pattern.slotValues)}
                  </TableCell>
                  <TableCell textAlign="right" style={TABLE_BODY_CELL_STYLE}>
                    <TertiaryButton
                      aria-label={t(`${NS}.editAria`, {
                        order: pattern.patternOrder,
                      })}
                      onClick={() => {
                        setModal({ kind: 'edit', index })
                      }}
                    >
                      {t('common:action.edit')}
                    </TertiaryButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </TableRoot>
      </Box>

      {modal.kind === 'create' && (
        <BasePatternModal
          initial={newPatternDraft(patterns)}
          mode="create"
          onClose={close}
          onSubmit={(value) => {
            onChange([...patterns, value])
            close()
          }}
          otherOrders={patterns.map((p) => p.patternOrder)}
        />
      )}
      {modal.kind === 'edit' && patterns[modal.index] !== undefined && (
        <BasePatternModal
          initial={draftFromPattern(patterns[modal.index])}
          mode="edit"
          onClose={close}
          onDelete={() => {
            onChange(patterns.filter((_, i) => i !== modal.index))
            close()
          }}
          onSubmit={(value) => {
            onChange(patterns.map((p, i) => (i === modal.index ? value : p)))
            close()
          }}
          otherOrders={patterns
            .filter((_, i) => i !== modal.index)
            .map((p) => p.patternOrder)}
        />
      )}
    </VStack>
  )
}
